/**
 * GameStateController - Handles game state management and update loop logic
 * 
 * Extracted from PPTHeroBaseScene.js to reduce monolithic structure and improve maintainability.
 * Responsible for:
 * - Game state transitions ('waiting', 'instructions', 'countdown', 'playing', 'completed', 'transitioning')  
 * - Update loop coordination and timing
 * - Level completion detection and goal checking
 * - Timer management and timeout handling
 * - UI update orchestration (throttled)
 */
export default class GameStateController {
  constructor(scene) {
    this.scene = scene;
    
    // Game state tracking
    this.lastUIUpdate = 0;
    this.lastGoalCheck = 0;
    
    // Performance throttling
    this.UI_UPDATE_INTERVAL = 100; // ms
    this.GOAL_CHECK_INTERVAL = 1000; // ms
  }

  /**
   * Main update loop - extracted from PPTHeroBaseScene.update()
   * Handles all game state logic in a structured, maintainable way
   */
  update() {
    // Safety check to prevent undefined access errors
    if (!this.scene || this.scene.levelComplete) return;
    
    // Don't run update logic during countdown or instructions - let those sequences complete
    if (this.isGameStateBlocked()) {
      return;
    }
    
    // Ensure all required properties are initialized (only during playing state)
    if (!this.areRequiredPropertiesInitialized()) {
      this.logMissingProperties();
      return;
    }

    // Calculate current timing
    const timing = this.calculateTiming();
    
    // Get current level data
    const levelData = this.getCurrentLevelData();
    if (!levelData) return;

    // Update UI elements (throttled for performance)
    this.updateUIElements(timing);

    // Handle timeout condition
    if (this.shouldHandleTimeout(timing.remaining)) {
      this.handleTimeout();
      return;
    }

    // Check for level completion (throttled to prevent infinite loops)
    this.checkLevelCompletion(levelData);
  }

  /**
   * Check if game state should block the update loop
   */
  isGameStateBlocked() {
    const blockedStates = ['countdown', 'instructions', 'transitioning'];
    return blockedStates.includes(this.scene.gameState);
  }

  /**
   * Validate that all required properties are initialized
   */
  areRequiredPropertiesInitialized() {
    return !!(
      this.scene.levels &&
      this.scene.currentLevel !== undefined &&
      this.scene.currentLevel !== null &&
      this.scene.startTime &&
      this.scene.timeLimit !== undefined
    );
  }

  /**
   * Log missing properties during playing state to aid debugging
   */
  logMissingProperties() {
    // Only log this during playing state to avoid spam during countdown
    if (this.scene.gameState === 'playing') {
      console.log("GameStateController: Required properties not initialized", {
        levels: !!this.scene.levels,
        currentLevel: this.scene.currentLevel,
        startTime: this.scene.startTime,
        timeLimit: this.scene.timeLimit
      });
    }
  }

  /**
   * Calculate current timing information
   */
  calculateTiming() {
    // Use consistent time format (seconds)
    const elapsed = (Date.now() / 1000) - this.scene.startTime;
    const remaining = Math.max(0, this.scene.timeLimit - elapsed);
    
    return { elapsed, remaining };
  }

  /**
   * Get current level data with proper ID-based lookup
   */
  getCurrentLevelData() {
    if (!this.scene.levels) return null;
    
    // FIXED: Find level by ID, not array index (Bain levels have IDs 22+ but array is 0-18)
    const levelData = this.scene.levels.find(level => level.id === this.scene.currentLevel) || 
                     this.scene.levels[this.scene.currentLevel];
    
    return levelData;
  }

  /**
   * Update UI elements with throttling for performance
   */
  updateUIElements(timing) {
    // Throttle UI updates to prevent performance issues (only update every 100ms)
    if (!this.lastUIUpdate || Date.now() - this.lastUIUpdate > this.UI_UPDATE_INTERVAL) {
      this.lastUIUpdate = Date.now();
      
      this.updateTopBarInfo();
      this.updateTimerDisplay(timing.remaining);
      this.updateProgressDisplay();
    }
  }

  /**
   * Update top bar information - FIXED to use existing currentRoundData
   */
  updateTopBarInfo() {
    // Use existing currentRoundData property instead of non-existent getCurrentRound() method
    const currentRoundData = this.scene.currentRoundData;
    const roundName = currentRoundData ? currentRoundData.name : "Practice";
    
    // Calculate the level number within the current round
    let levelNumberInRound = this.scene.currentLevel + 1; // Default fallback
    if (currentRoundData && currentRoundData.levels && this.scene.currentLevel !== null) {
      const levelIndexInRound = currentRoundData.levels.indexOf(this.scene.currentLevel);
      levelNumberInRound = levelIndexInRound >= 0 ? levelIndexInRound + 1 : this.scene.currentLevel + 1;
    }
    
    this.scene.updateTopBarInfo(roundName, levelNumberInRound, this.scene.score || 0);
  }

  /**
   * Update timer display
   */
  updateTimerDisplay(remaining) {
    // Update timer display for timed levels (throttled, no logging spam)
    if (this.scene.currentLevel >= 0 && remaining > 0) {
      // Use the UIController to show timer display
      if (this.scene.pptUIController) {
        this.scene.pptUIController.showTimerDisplay(remaining, remaining <= 30);
      }
    } else {
      // Use the UIController to hide timer display
      if (this.scene.pptUIController) {
        this.scene.pptUIController.hideTimerDisplay();
      }
    }
  }

  /**
   * Update progress display - FIXED to use existing currentRoundData
   */
  updateProgressDisplay() {
    // Use existing currentRoundData property instead of non-existent getCurrentRound() method  
    const currentRoundData = this.scene.currentRoundData;
    const totalLevelsInRound = (currentRoundData && currentRoundData.levels) ? 
      currentRoundData.levels.length : this.scene.levels.length;
    
    const levelInRound = (currentRoundData && currentRoundData.levels) ? 
      currentRoundData.levels.indexOf(this.scene.currentLevel) + 1 : this.scene.currentLevel + 1;
    
    // Calculate efficiency score
    const efficiency = this.scene.calculateEfficiency();
    this.scene.showProgressDisplay(levelInRound, totalLevelsInRound, efficiency);
  }

  /**
   * Check if timeout should be handled
   */
  shouldHandleTimeout(remaining) {
    return this.scene.currentLevel !== 0 && remaining <= 0;
  }

  /**
   * Handle timeout condition
   */
  handleTimeout() {
    this.scene.score -= 50;
    
    // Save level performance data with timeout
    this.scene.saveLevelAnalytics(false);
    
    this.scene.nextLevelImmediate();
  }

  /**
   * Check for level completion with throttling
   */
  checkLevelCompletion(levelData) {
    // If the goal is met => levelCleared (throttled to prevent infinite loops)
    if (levelData.goal && 
        typeof levelData.goal === 'function' && 
        !this.scene.levelComplete && 
        this.scene.gameState === 'playing') {
      
      // Only check goal every 1000ms to prevent spam and freezing
      if (!this.lastGoalCheck || Date.now() - this.lastGoalCheck > this.GOAL_CHECK_INTERVAL) {
        this.lastGoalCheck = Date.now();
        
        const goalResult = levelData.goal(this.scene);
        
        if (goalResult && !this.scene.levelComplete) {
          console.log('🎉 LEVEL COMPLETED! Calling levelCleared()');
          this.scene.levelCleared();
        }
      }
    }
  }

  /**
   * Set game state with validation
   */
  setState(newState) {
    const validStates = ['waiting', 'instructions', 'countdown', 'playing', 'completed', 'transitioning'];
    
    if (!validStates.includes(newState)) {
      console.error('GameStateController: Invalid state:', newState);
      return;
    }
    
    console.log(`GameStateController: ${this.scene.gameState} -> ${newState}`);
    this.scene.gameState = newState;
  }

  /**
   * Get current game state
   */
  getState() {
    return this.scene.gameState;
  }

  /**
   * Check if currently in a specific state
   */
  isState(state) {
    return this.scene.gameState === state;
  }

  /**
   * Reset state tracking variables
   */
  reset() {
    this.lastUIUpdate = 0;
    this.lastGoalCheck = 0;
  }

  /**
   * Clear all shapes from the scene - delegated to ShapeManager
   */
  clearAllShapes() {
    if (this.scene.shapeManager) {
      this.scene.shapeManager.clearAllShapes();
    }
  }

  /**
   * Hide instructions overlay - delegated to RexUIManager
   */
  hideInstructionsOverlay() {
    if (this.scene.rexUIManager) {
      this.scene.rexUIManager.hideInstructionsOverlay();
    }
  }

  /**
   * Get current round data - use existing currentRoundData property
   */
  getCurrentRound() {
    return this.scene.currentRoundData;
  }

  /**
   * Shutdown method for cleanup
   */
  shutdown() {
    this.reset();
  }
}
